<script setup lang="ts">
import { computed } from "vue";

import Block from "@/components/ui/atoms/Block.vue";
import MasterBlock from "@/components/ui/atoms/MasterBlock.vue";
import PillContainer from "@/components/ui/atoms/PillContainer.vue";
import type { Branch, Course } from "@/shared/explorerTypes";

interface Props {
  course: Course;
  currentBranch: Branch;
  container: string;
}

const props = defineProps<Props>();

const dynamicImage = computed(() => {
  return new URL(
    `../../assets/img/${props.currentBranch.slug}_es-para-mi.svg`,
    import.meta.url,
  ).href;
});
</script>

<template>
  <MasterBlock theme="shadow">
    <section :class="[container]">
      <div class="to-learn">
        <Block theme="clear" class="learn">
          <div class="container__header">
            <h2>¿QUÉ VAS A APRENDER?</h2>
          </div>
          <PillContainer
            theme="clear"
            shape="square"
            :class="['learn__item']"
            v-for="(keyPoint, index) in course.key_points_list"
            :key="index"
          >
            <span
              class="learn__item__brand"
              :style="`background-color: ${currentBranch.base_color}`"
            >
              <img src="@img/m-letter-black.svg" alt="Logo" />
            </span>
            <span class="learn__item__text">
              {{ keyPoint }}
            </span>
          </PillContainer>
        </Block>
        <Block theme="off-black" class="for-me">
          <div class="for-me__img">
            <img :src="dynamicImage" alt="Para mi" />
          </div>

          <h2 class="no-margins">¿ES PARA MI?</h2>
          <p class="no-margins">
            ¡Por supuesto! Todos los contenidos que se imparten en el curso van
            acompañados de ejercicios prácticos, tareas fundamentales que te ayudarán a
            absorber todo el conocimiento mientras practicas de forma divertida. Nuestra
            filosofía es que aprendas mientras te diviertes.
          </p>
        </Block>
      </div>
    </section>
  </MasterBlock>
</template>

<style scoped lang="scss">
@use "@/styles/settings/vars";

.explorer-container {
  padding: 1em;
  max-width: 1200px;
}

.to-learn {
  display: grid;
  grid-template-columns: minmax(0, 2fr) 1fr;
  gap: 1em;
}

.for-me {
  display: flex;
  flex-direction: column;
  gap: 1em;

  &__img {
    width: 100%;

    img {
      max-width: 200px;
    }
  }
}

.learn {
  display: flex;
  flex-direction: column;
  gap: 0.5em;

  .pill-container > * {
    align-self: inherit;
  }

  &__item {
    display: flex;
    align-items: baseline;
    background-color: vars.$dust;
    padding: 0.5em;
    border-radius: 0.5em;
    color: vars.$off-black;
    max-width: calc(100% - 1em);
    margin-right: 1em;

    &__brand {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 30px;
      height: 30px;
      background-color: vars.$poly-fuchsia;
      border-radius: 50%;
      margin-right: 0.5em;
    }

    &__text {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      flex: 1;
      white-space: normal;
      word-wrap: break-word;
    }
  }
}

@media (width < 600px) {
  .to-learn {
    grid-template-columns: minmax(100%, 1fr);
  }

  .learn__item {
    width: 100%;
    margin-right: 0;
    max-width: unset;
  }
}
</style>
